'use client';

import { cn } from '@core/utils';
import { debounce } from 'es-toolkit';
import type { CSSProperties } from 'react';
import {
  createContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { SwipeableHandlers } from 'react-swipeable';
import { useSwipeable } from 'react-swipeable';
import { Transition } from 'react-transition-group';
import { create } from 'zustand';

import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock/useBodyScrollLock';
import type { PortalProps } from '../Portal/Portal';
import { Portal } from '../Portal/Portal';
import { Contents } from './_components/Contents';
import { Handle } from './_components/Handle';
import { HandleWrapper } from './_components/HandleWrapper';

interface BottomDrawerProps {
  opened?: boolean;
  onClose?: () => void;
  className?: {
    backdrop?: string;
    drawer?: string;
  };
  children: React.ReactNode;
  unmountOnExit?: boolean;
  mountOnEnter?: boolean;
  duration?: number;
  renderTo?: PortalProps['renderTo'];
}

interface BottomDrawerContextProps {
  swipeableHandlers: SwipeableHandlers;
  isVisible: boolean;
  onClose: BottomDrawerProps['onClose'];
}

export const BottomDrawerContext = createContext<
  BottomDrawerContextProps | undefined
>(undefined);

interface BottomDrawerStore {
  drawerMap: Map<string, string | null>;
  setDrawerMap: (drawerMap: Map<string, string | null>) => void;

  currentDrawerId: string | null;
  setCurrentDrawerId: (id: string | null) => void;
}

export const useBottomDrawerStore = create<BottomDrawerStore>((set, get) => ({
  drawerMap: new Map(),
  setDrawerMap: (drawerMap) => set({ drawerMap }),

  currentDrawerId: null,
  setCurrentDrawerId: (id) => {
    const { drawerMap, currentDrawerId: prevDrawerId } = get();

    if (id) {
      drawerMap.set(id, prevDrawerId);
    }

    set({ currentDrawerId: id, drawerMap });
  },
}));

const Provider = ({
  opened = false,
  onClose,
  children,
  className,
  unmountOnExit,
  mountOnEnter,
  duration = 300,
  renderTo,
}: BottomDrawerProps) => {
  const id = useId();

  const { currentDrawerId, drawerMap, setCurrentDrawerId, setDrawerMap } =
    useBottomDrawerStore();

  useEffect(() => {
    if (opened) return setCurrentDrawerId(id);

    const prevDrawerId = drawerMap.get(id);

    if (prevDrawerId && drawerMap.has(prevDrawerId)) {
      setCurrentDrawerId(prevDrawerId);
    }

    drawerMap.delete(id);
    setDrawerMap(drawerMap);
  }, [drawerMap, id, opened, setCurrentDrawerId, setDrawerMap]);

  const isCurrentDrawer = currentDrawerId === id;

  const isVisible = opened && isCurrentDrawer;

  const [deltaY, setDeltaY] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const isCloseLine = useRef(false);

  useLayoutEffect(() => {
    if (!deltaY && isCloseLine.current) {
      isCloseLine.current = false;
      onClose?.();
    }
  }, [deltaY, onClose]);

  const swipeableHandlers = useSwipeable({
    onSwipedDown: debounce(
      ({ velocity }) => {
        setDeltaY(0);
        if (1 < velocity) {
          isCloseLine.current = false;
          onClose?.();
        }
      },
      33,
      { edges: ['leading'] },
    ),
    onSwiping: ({ deltaY }) => {
      if (!drawerRef.current) return;

      const { offsetHeight } = drawerRef.current;

      const closingLine = offsetHeight * 0.7;

      isCloseLine.current = closingLine < deltaY;

      setDeltaY(deltaY);
    },
    trackMouse: true,
  });

  //* mobile keyboard 대응
  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    const updateHeight = () => {
      setViewportHeight(visualViewport.height);
    };

    visualViewport.addEventListener('resize', updateHeight);

    return () => visualViewport.removeEventListener('resize', updateHeight);
  }, []);

  useBodyScrollLock(isVisible);

  return isCurrentDrawer ? (
    <Portal renderTo={renderTo}>
      <BottomDrawerContext.Provider
        value={{ isVisible, onClose, swipeableHandlers }}
      >
        <Transition
          appear
          in={isVisible}
          mountOnEnter={mountOnEnter}
          nodeRef={containerRef}
          timeout={{ appear: 0, enter: 0, exit: duration }}
          unmountOnExit={unmountOnExit}
        >
          {(state) => {
            const [backdropStyles, transitionStyles] = (() => {
              const transformStyles =
                deltaY <= 0
                  ? undefined
                  : ({
                      transform: `translate3d(0, ${deltaY}px, 0)`,
                      transitionProperty: 'none',
                    } satisfies CSSProperties);

              switch (state) {
                case 'entering':
                case 'exiting':
                  return [
                    {
                      opacity: '0',
                      transitionProperty: 'opacity',
                      transitionDuration: `${duration}ms`,
                    },
                    {
                      transform: 'translate3d(0, 100%, 0)',
                      transitionProperty: 'transform',
                      transitionDuration: `${duration}ms`,
                      ...transformStyles,
                    },
                  ];
                case 'entered':
                  return [
                    {
                      opacity: '1',
                      transitionProperty: 'opacity',
                      transitionDuration: `${duration}ms`,
                    },
                    {
                      transitionProperty: 'transform',
                      transitionDuration: `${duration}ms`,
                      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                      ...transformStyles,
                    },
                  ];
                case 'exited':
                case 'unmounted':
                  return [
                    { display: 'none' },
                    { display: 'none', ...transformStyles },
                  ];

                default:
                  return [];
              }
            })();

            return (
              <div ref={containerRef}>
                <div
                  className={cn(
                    'absolute left-0 top-0 z-10 size-full bg-Gray-black/70 duration-300',
                    className?.backdrop,
                  )}
                  style={backdropStyles}
                  role="button"
                  tabIndex={0}
                  aria-label="드로어 닫기"
                  onClick={onClose}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClose?.();
                    }
                  }}
                />
                <div
                  ref={drawerRef}
                  className={cn(
                    'absolute bottom-0 left-0 z-20 flex w-full flex-col rounded-t-large bg-Gray-white shadow-lg',
                    className?.drawer,
                  )}
                  style={{
                    ...transitionStyles,
                    maxHeight: viewportHeight
                      ? `${viewportHeight - 16}px`
                      : 'calc(100dvh - 1rem)',
                  }}
                >
                  {children}
                </div>
              </div>
            );
          }}
        </Transition>
      </BottomDrawerContext.Provider>
    </Portal>
  ) : null;
};

export const BottomDrawer = Object.assign(Provider, {
  HandleWrapper,
  Handle,
  Contents,
});
